import React, { FC } from 'react';
import { Route, Switch, useParams, useRouteMatch } from 'react-router';
import { LoanFormatNavigation } from '../shared/navigation/LoanFormatNavigation';
import { LoanFormat } from '../../models/LoanFormat';
import SubNavigationLayout from '../shared/navigation/SubNavigationLayout';
import { LinkContainer } from '../shared/navigation/NavigationLinksContainer';
import LinkWithIconLeft from '../shared/common/LinkWithIconLeft';
import { LoanFormatIcon } from '../shared/common/GildIcon';
import { BOLINDA_ICON_LIBX, MATERIAL_ICON_CAMPAIGNS, MATERIAL_ICON_LANGUAGE } from '../shared/common/Button/Icon';
import { NavigationLink } from '../shared/navigation/NavigationLink';
import AgeCategorySelector from '../shared/common/AgeCategorySelector';
import { useTranslation } from 'react-i18next';
import LanguageLearningNavigation from '../shared/navigation/LanguageLearningNavigation';
import UnlimitedNavigation from '../shared/navigation/UnlimitedNavigation';
import LibxNavigation from './LibxNavigation';
import { CAMPAIGN_PRODUCTS, useFeature } from '../shared/utils/togglz';
import ShopLanguageDropDown from '../shared/navigation/ShopLanguageDropDown';

type Props = {
    readonly isSticky: boolean;
};

const ShopLanguageNavigation: FC<Props> = ({ isSticky }) => {
    const { t } = useTranslation();
    const { path, url } = useRouteMatch();
    const campaignsEnabled = useFeature(CAMPAIGN_PRODUCTS);
    const { shopLanguage } = useParams<{ shopLanguage: string }>();

    return (
        <Switch>
            <Route path={`${path}/eaudiobooks`}>
                <LoanFormatNavigation
                    isSticky={isSticky}
                    loanFormat={LoanFormat.eAudiobooks}
                    shopLanguage={shopLanguage}
                />
            </Route>
            <Route path={`${path}/ebooks`}>
                <LoanFormatNavigation isSticky={isSticky} loanFormat={LoanFormat.eBooks} shopLanguage={shopLanguage} />
            </Route>
            <Route path={`${path}/epress`}>
                <LoanFormatNavigation
                    ignoreNewReleases={true}
                    isSticky={isSticky}
                    loanFormat={LoanFormat.ePress}
                    shopLanguage={shopLanguage}
                />
            </Route>
            <Route path={`${path}/languagelearning`}>
                <LanguageLearningNavigation isSticky={isSticky} shopLanguage={shopLanguage} />
            </Route>
            <Route path={`${path}/unlimited`}>
                <UnlimitedNavigation isSticky={isSticky} shopLanguage={shopLanguage} />
            </Route>
            <Route path={`${path}/libxchange`}>
                <LibxNavigation isSticky={isSticky} shopLanguage={shopLanguage} />
            </Route>
            <Route>
                <SubNavigationLayout
                    left={shopLanguage ? <ShopLanguageDropDown currentShopLanguage={shopLanguage} /> : null}
                    center={
                        <LinkContainer>
                            <li>
                                <LinkWithIconLeft to={`${url}/eaudiobooks`}>
                                    <LoanFormatIcon aria-hidden={true} icon="eaudiobook-filled" />
                                    <span>{t('menu.shop.eaudiobooksMain')}</span>
                                </LinkWithIconLeft>
                            </li>
                            <li>
                                <LinkWithIconLeft to={`${url}/ebooks`}>
                                    <LoanFormatIcon aria-hidden={true} icon="ebook-filled" />
                                    <span>{t('menu.shop.ebooksMain')}</span>
                                </LinkWithIconLeft>
                            </li>
                            <li>
                                <LinkWithIconLeft to={`${url}/epress`}>
                                    <LoanFormatIcon aria-hidden={true} icon="emagazine-filled" />
                                    <span>{t('menu.shop.epressMain')}</span>
                                </LinkWithIconLeft>
                            </li>
                            <li>
                                <LinkWithIconLeft to={`${url}/languagelearning`}>
                                    <i className="material-icons" aria-hidden={true}>
                                        {MATERIAL_ICON_LANGUAGE}
                                    </i>
                                    <span>{t('menu.shop.languages.languageLearning')}</span>
                                </LinkWithIconLeft>
                            </li>
                            {campaignsEnabled ? (
                                <li>
                                    <LinkWithIconLeft to={`${url}/unlimited`}>
                                        <i className="material-icons" aria-hidden={true}>
                                            {MATERIAL_ICON_CAMPAIGNS}
                                        </i>
                                        <span>{t('menu.shop.unlimitedMain')}</span>
                                    </LinkWithIconLeft>
                                </li>
                            ) : null}
                            <li>
                                <NavigationLink to={`${url}/libxchange`} className="material-link nav-new">
                                    <i className="bolinda-icons" aria-hidden={true}>
                                        {BOLINDA_ICON_LIBX}
                                    </i>
                                    <span>
                                        {t('menu.shop.libxchangeMain')}
                                        {!campaignsEnabled && !isSticky ? <span className="new-label">New</span> : null}
                                    </span>
                                </NavigationLink>
                            </li>
                        </LinkContainer>
                    }
                    right={!isSticky ? <AgeCategorySelector /> : null}
                    isSticky={isSticky}
                    width="15"
                />
            </Route>
        </Switch>
    );
};

ShopLanguageNavigation.displayName = 'ShopLanguageNavigation';

export default ShopLanguageNavigation;
